import { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { DragBox } from "./DragBox";
import { PanelSection } from "./PanelSection";
import { HighlightPicker } from "./HighlightPicker";
import { ShapeLayer, ShapeToolbar } from "./ShapeLayer";
import { BarsLabelInput } from "./BarsLabelInput";
import { FRAME_LABELS, framePreviewUrl, loadFrame, loadThumbFont, renderThumbnail, downloadCanvas } from "@/lib/thumbnail";

// Pick a frame, type the sell text, then drag/resize text, Voice Bars, arrows and circles and download a 1280x720 JPG.
export function ThumbnailEditor({ videoId, thumb, setThumb, onSave, barsLabel, setBarsLabel }) {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const [img, setImg] = useState(null);
  const [fontReady, setFontReady] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { loadThumbFont().then(() => setFontReady(true)); }, []);
  useEffect(() => {
    let live = true;
    setImg(null);
    loadFrame(videoId, thumb.frame).then((i) => live && setImg(i)).catch(() => {});
    return () => { live = false; };
  }, [videoId, thumb.frame]);
  useEffect(() => { renderThumbnail(canvasRef.current, img, thumb, barsLabel.trim() || "Reaction"); }, [img, thumb, fontReady, barsLabel]);

  const update = (patch, save = true) => { const next = { ...thumb, ...patch }; setThumb(next); if (save) onSave(next); };
  const commit = () => onSave(thumb);
  const stageRatio = () => { const r = stageRef.current.getBoundingClientRect(); return r.width / r.height; };
  const bars = { ...thumb.bars_box, h: thumb.bars_box.w * (16 / 9) };

  return (
    <div className="flex flex-col gap-6" data-testid="thumbnail-editor">
      <div ref={stageRef} onPointerDown={() => setSelected(null)} className="relative w-full rounded-[4px] overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }} data-testid="thumbnail-stage">
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" data-testid="thumbnail-canvas" />
        <DragBox stageRef={stageRef} testId="thumb-bars-box" label="Voice Bars" box={bars} ratio={stageRatio}
          onChange={({ x, y, w }) => update({ bars_box: { x, y, w } }, false)} onCommit={commit} />
        <DragBox stageRef={stageRef} testId="thumb-text-box" label="text" box={thumb.text_box}
          onChange={(b) => update({ text_box: b }, false)} onCommit={commit} />
        <ShapeLayer stageRef={stageRef} shapes={thumb.shapes} setShapes={(shapes) => update({ shapes }, false)} commit={commit}
          selected={selected} setSelected={setSelected} />
      </div>
      <ShapeToolbar shapes={thumb.shapes} onSave={(shapes) => update({ shapes })} selected={selected} setSelected={setSelected} />
      <FramePicker videoId={videoId} frame={thumb.frame} onPick={(frame) => update({ frame })} />
      <PanelSection label="Thumbnail Title" testId="thumb-text-section">
        <input value={thumb.text} onChange={(e) => update({ text: e.target.value })} maxLength={120} data-testid="thumb-text-input"
          placeholder="Type the big text, e.g. I CAN'T BELIEVE THIS"
          className="w-full bg-white border border-zinc-200 hover:border-zinc-300 rounded-[4px] px-3 py-2.5 text-sm text-zinc-900 outline-none focus:border-rose-500 transition-colors" />
        <p className="text-xs text-zinc-500">Shown in Impact capitals. Drag the dashed boxes to move them and pull a corner to resize; the text grows to fill its box.</p>
        <HighlightPicker text={thumb.text} highlight={thumb.highlight} onChange={(highlight) => update({ highlight })} />
      </PanelSection>
      <BarsLabelInput value={barsLabel} onChange={setBarsLabel} testId="thumb-bars-label-input" />
      <div className="flex justify-end">
        <button type="button" onClick={() => downloadCanvas(canvasRef.current)} disabled={!img} data-testid="thumb-download-btn"
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm font-semibold rounded-[4px] px-6 py-3 transition-colors">
          <Download size={15} /> Download Thumbnail
        </button>
      </div>
    </div>
  );
}

function FramePicker({ videoId, frame, onPick }) {
  return (
    <PanelSection label="Video Frame" testId="thumb-frame-section">
      <div className="grid grid-cols-4 gap-3">
        {FRAME_LABELS.map((l, n) => (
          <button key={n} type="button" onClick={() => onPick(n)} data-testid={`thumb-frame-${n}`}
            className={`group flex flex-col gap-1.5 text-left transition-opacity ${frame === n ? "" : "opacity-70 hover:opacity-100"}`}>
            <img src={framePreviewUrl(videoId, n)} alt={`${l} frame`}
              className={`w-full aspect-video object-cover rounded-[4px] border-2 transition-colors ${frame === n ? "border-rose-500" : "border-transparent"}`} />
            <span className="text-xs text-zinc-500">{l}</span>
          </button>
        ))}
      </div>
    </PanelSection>
  );
}
